import { type UseMutationResponse } from '@urql/vue';
import toast from '~/components/Toast';
import type {
  Exact,
  Feature,
  License,
  LicensingSessionCheckoutInput,
  PaymentGatewayPortalInput,
  PlanType,
  RequestCheckoutSessionDataMutation,
  RequestPaymentGatewayPortalDataMutation,
} from '~/graphql';
import { upsellUrl } from '~/utils/constants';

export enum FeatureKey {
  FILES_UNLIMITED = 'files_unlimited',
  AI_BASIC = 'ai_basic',
  CALENDAR_PERSONAL_EVENTS = 'calendar_personal_events',
  CALENDAR_VACATION = 'calendar_vacation',
  CALENDAR_ADDITIONAL_PERIODS = 'calendar_additional_periods',
  COLLABORATION_TEAMTEACHING = 'collaboration_teamteaching',
  INTEGRATION_ONENOTE = 'integration_onenote',
  CLASSREGISTER_COPY = 'classregister_copy',
  INTEGRATION_TIMETABLE = 'integration_timetable',
  COLLABORATION_COURSE_TEMPLATES = 'collaboration_course_templates',
  COLLABORATION_SUBSTITUTIONS = 'collaboration_substitutions',
  STUDENT_ACCESS = 'student_access',
  AI_ADVANCED = 'ai_advanced',
  CURRICULUM = 'curriculum',
  PRINT_WEEKLY_PLAN = 'print_weekly_plan',
  PRINT_YEAR = 'print_year',
  TODOS = 'todos',
  ARCHIVE = 'archive',
  CREATE_COURSE = 'create_course',
  PRESENTATION_MODE = 'presentation_mode',
}

export default function useLicense() {
  const { $t } = useNuxtApp();
  const me = useState('me');

  const showUpgradeModal = useState<boolean>('showUpgradeModal', () => false);
  let requestCheckoutSessionDataMutation: UseMutationResponse<
    RequestCheckoutSessionDataMutation,
    Exact<{ licensingSessionCheckoutInput: LicensingSessionCheckoutInput }>
  > | null = null;
  let requestPaymentGatewayPortalDataMutation: UseMutationResponse<
    RequestPaymentGatewayPortalDataMutation,
    Exact<{ paymentGatewayPortalInput: PaymentGatewayPortalInput }>
  > | null = null;

  function init() {
    requestCheckoutSessionDataMutation = useRequestCheckoutSessionDataMutation();
    requestPaymentGatewayPortalDataMutation = useRequestPaymentGatewayPortalDataMutation();
  }

  function getLicenses(): License[] {
    if (me.value && me.value.licenses) {
      return me.value.licenses;
    } else {
      console.log('checking for non existing licenses');
      return [];
    }
  }

  function getAllFeatures(): FeatureKey[] {
    const featureKeys: FeatureKey[] = getLicenses()
      .map((license: License) =>
        license.plan?.features.map((feature: Feature) => {
          const indexOf = Object.values(FeatureKey).indexOf(feature.key as unknown as FeatureKey);
          return Object.keys(FeatureKey)[indexOf] as FeatureKey;
        })
      )
      .flat(1);
    // remove duplicates
    return [...new Set(featureKeys)];
  }

  function canUseFeature(feature: FeatureKey): boolean {
    // check if feature is in list of all features
    const indexOf = Object.values(FeatureKey).indexOf(feature);
    const featureKey = Object.keys(FeatureKey)[indexOf] as FeatureKey;
    return getAllFeatures().includes(featureKey);
  }

  function canUseFeatureOrAlert(feature: FeatureKey): boolean {
    if (!canUseFeature(feature)) {
      openUpgradeModal();
      return false;
    }
    return true;
  }

  function canUseFeatureOrRedirect(feature: FeatureKey): boolean {
    if (!canUseFeature(feature)) {
      navigateTo('/account/discoverpro');
      return false;
    }
    return true;
  }

  function hasPaidPlan(): boolean {
    return getLicenses().some((license: License) => license.plan.type === PlanType.Paid);
  }

  function openUpgradeModal() {
    showUpgradeModal.value = true;
  }

  function closeUpgradeModal() {
    showUpgradeModal.value = false;
  }

  async function startUpgradeProcess() {
    const { data, error } = await requestCheckoutSessionDataMutation.executeMutation({
      licensingSessionCheckoutInput: {
        planKey: 'teacher_pro',
      },
    });
    if (error) {
      toast.error($t('generic_error'));
      return;
    }
    const url = data.requestCheckoutSessionData.url;
    const newTabOpened = window.open(url, '_blank');
    if (!newTabOpened) {
      navigateTo(url, { external: true });
    }
  }

  async function startManageSubscription(licenseId: string) {
    const { data, error } = await requestPaymentGatewayPortalDataMutation.executeMutation({
      paymentGatewayPortalInput: {
        licenseId,
      },
    });
    if (error) {
      toast.error($t('error_generic'));
      return;
    }
    const url = data.requestPaymentGatewayPortalData.url;
    const newTabOpened = window.open(url, '_blank');
    if (!newTabOpened) {
      navigateTo(url, { external: true });
    }
  }

  function openSchoolLicense() {
    const newTabOpened = window.open(upsellUrl, '_blank');
    if (!newTabOpened) {
      navigateTo(upsellUrl, { external: true });
    }
  }

  function textForFeatures(license: License): string[] {
    // we don't want to show all features - here we control what we show
    const features: string[] = [];
    const featureIds: string[] = license.plan.features.map((feature) => feature.key);

    if (featureIds.indexOf('integration_timetable') >= 0) {
      features.push($t('upgrade.feature.integration_timetable'));
    }

    if (featureIds.indexOf('files_unlimited') >= 0) {
      features.push($t('upgrade.feature.files_unlimited'));
    } else {
      features.push($t('upgrade.feature.files_restricted'));
    }

    if (
      featureIds.indexOf('calendar_personal_events') >= 0 ||
      featureIds.indexOf('calendar_vacation') >= 0 ||
      featureIds.indexOf('calendar_additional_periods') >= 0
    ) {
      features.push($t('upgrade.feature.daily_changes'));
    }

    if (featureIds.indexOf('student_access') >= 0) {
      features.push($t('upgrade.feature.student_access'));
    }

    if (featureIds.indexOf('collaboration_substitutions') >= 0) {
      features.push($t('upgrade.feature.collaboration_substitutions'));
    }

    if (featureIds.indexOf('collaboration_course_templates') >= 0) {
      features.push($t('upgrade.feature.collaboration_course_templates'));
    }

    if (featureIds.indexOf('collaboration_teamteaching') >= 0) {
      features.push($t('upgrade.feature.collaboration_teamteaching'));
    }

    if (featureIds.indexOf('ai_basic') >= 0) {
      features.push($t('upgrade.feature.ai_basic'));
    }

    if (featureIds.indexOf('ai_advanced') >= 0) {
      features.push($t('upgrade.feature.ai_advanced'));
    }

    if (featureIds.indexOf('presentation_mode') >= 0) {
      features.push($t('upgrade.feature.presentation'));
    }

    return features;
  }

  return {
    init,
    getLicenses,
    canUseFeature,
    getAllFeatures,
    canUseFeatureOrAlert,
    canUseFeatureOrRedirect,
    hasPaidPlan,
    openUpgradeModal,
    closeUpgradeModal,
    showUpgradeModal,
    startUpgradeProcess,
    openSchoolLicense,
    startManageSubscription,
    textForFeatures,
  };
}
